import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { alpha, useTheme } from '@mui/material/styles';
import { toast } from '../../toast';

type UploadFileProps = {
  uploadedFile: File[] | null;
  setUploadedFile: (file: File[] | null) => void;
  validTypes?: string[];
  isMultiple?: boolean;
  disabled?: boolean;
};

export const UploadFileComponent = ({
  uploadedFile,
  setUploadedFile,
  validTypes = [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/pdf',
    'application/msword',
    'video/mp4',
  ],
  isMultiple = false,
  disabled = false,
}: UploadFileProps) => {
  const theme = useTheme();
  const [isDragOver, setIsDragOver] = useState(false);

  const validateFile = (file: File) => {
    return validTypes.includes(file.type);
  };

  const fileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    const files = event.dataTransfer?.files;
    if (files && files.length) {
      if (!isMultiple && files.length > 1) {
        toast.error('Only one file allowed', { position: 'bottom-center' });
        return;
      }
      const validFiles = Array.from(files).filter((file) => validateFile(file));
      if (validFiles.length > 0) {
        handleNewFiles(Array.from(files));
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!disabled) setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const removeFile = (index: number) => {
    if (uploadedFile && uploadedFile.length > 0) {
      const updatedFile = [...uploadedFile];
      updatedFile.splice(index, 1);
      setUploadedFile(updatedFile.length > 0 ? updatedFile : null);
    }
  };

  const truncateFileName = (fileName: string, maxLength: number = 30) => {
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.slice(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.slice(
      0,
      maxLength - 3 - (extension?.length || 0),
    );
    return `${truncatedName}...${extension}`;
  };

  const addFileViaInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length) {
      if (!isMultiple && files.length > 1) {
        toast.error('Only one file allowed', { position: 'bottom-center' });
        return;
      }
      const validFiles = Array.from(files).filter((file) => validateFile(file));
      if (validFiles.length > 0) {
        handleNewFiles(Array.from(files));
      }
    }
  };

  const handleNewFiles = (newFiles: File[]) => {
    const validNewFiles = newFiles.filter(validateFile);
    if (validNewFiles.length === 0) {
      toast.error('No valid files selected.', { position: 'bottom-center' });
      return;
    }

    const uniqueNewFiles = validNewFiles.filter(
      (newFile, index, self) =>
        index ===
        self.findIndex(
          (f) => f.name === newFile.name && f.size === newFile.size,
        ),
    );

    const nonDuplicateFiles = uploadedFile
      ? uniqueNewFiles.filter(
          (newFile) =>
            !uploadedFile.some(
              (existingFile) =>
                existingFile.name === newFile.name &&
                existingFile.size === newFile.size,
            ),
        )
      : uniqueNewFiles;

    if (!isMultiple) {
      if (uploadedFile && uploadedFile.length > 0) {
        toast.error('Only one file allowed', { position: 'bottom-center' });
        return;
      }
      setUploadedFile([nonDuplicateFiles[0]]);
    } else {
      const updatedFiles = uploadedFile
        ? [...uploadedFile, ...nonDuplicateFiles]
        : nonDuplicateFiles;
      setUploadedFile(updatedFiles);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <input
        type="file"
        onChange={addFileViaInput}
        id="upload-file-input"
        style={{ display: 'none' }}
        multiple={isMultiple}
        disabled={disabled}
      />

      <Paper
        variant="outlined"
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={fileDrop}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3.5,
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: isDragOver
            ? 'primary.main'
            : disabled
              ? 'divider'
              : 'rgba(0, 0, 0, 0.15)',
          borderRadius: '16px',
          bgcolor: isDragOver
            ? alpha(theme.palette.primary.main, 0.04)
            : disabled
              ? 'action.hover'
              : 'background.paper',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: disabled ? 'divider' : 'primary.main',
          },
        }}
        onClick={() => {
          if (!disabled) {
            document.getElementById('upload-file-input')?.click();
          }
        }}
      >
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1.5,
            color: 'primary.main',
          }}
        >
          <CloudUploadRoundedIcon sx={{ fontSize: 28 }} />
        </Box>

        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: disabled ? 'text.disabled' : 'text.primary',
            mb: 0.5,
            textAlign: 'center',
          }}
        >
          Drag & Drop your files here - PDF or DOCX
        </Typography>

        <Typography
          sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            mb: 2,
            textAlign: 'center',
          }}
        >
          or click to browse from your computer
        </Typography>

        <Button
          size="small"
          variant="outlined"
          color="primary"
          disabled={disabled}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontSize: '0.8125rem',
            fontWeight: 600,
            px: 2.5,
            py: 0.75,
            mb: 1.5,
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) {
              document.getElementById('upload-file-input')?.click();
            }
          }}
        >
          Browse Files
        </Button>

        <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled' }}>
          Accepted file types: PDF, DOCX
        </Typography>
      </Paper>

      {/* Uploaded Files Chips */}
      {uploadedFile && uploadedFile.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
          {uploadedFile.map((file, index) => (
            <Chip
              key={index}
              icon={<InsertDriveFileRoundedIcon sx={{ fontSize: 16 }} />}
              label={truncateFileName(file.name, 28)}
              onDelete={() => removeFile(index)}
              deleteIcon={<CloseRoundedIcon sx={{ fontSize: 14 }} />}
              sx={{
                borderRadius: '8px',
                bgcolor: 'action.hover',
                fontWeight: 500,
                fontSize: '0.8125rem',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
